/*
 * Where: src/features/nfc/services/extensionBridgeClient.js
 * What: Browser-to-extension bridge for NFC UID reading requests.
 * Why: Desktop browsers cannot directly access USB NFC readers, so task 4 uses an extension bridge.
 */

const WEB_MESSAGE_SOURCE = 'recommend-support-web';
const EXTENSION_MESSAGE_SOURCE = 'recommend-support-extension';
const READ_UID_REQUEST_TYPE = 'NFC_BRIDGE_READ_UID';
const READ_UID_RESPONSE_TYPE = 'NFC_BRIDGE_READ_UID_RESULT';

export const BRIDGE_READ_TIMEOUT_MS = 30000;

const createBridgeError = (message, code) => {
    const error = new Error(message);
    error.code = code;
    return error;
};

const normalizeCardUid = (uid) => {
    if (!uid) {
        return '';
    }
    return String(uid).replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
};

const createRequestId = () => {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const readCardUidViaExtension = ({ timeoutMs = BRIDGE_READ_TIMEOUT_MS, signal } = {}) => {
    if (typeof window === 'undefined' || typeof window.postMessage !== 'function') {
        return Promise.reject(
            createBridgeError('ブラウザ環境でのみ読み取り可能です。', 'BROWSER_UNAVAILABLE')
        );
    }

    return new Promise((resolve, reject) => {
        if (signal && signal.aborted) {
            reject(createBridgeError('読み取り要求が中断されました。', 'ABORTED'));
            return;
        }

        const requestId = createRequestId();
        let timeoutId = null;

        const handleAbort = () => {
            cleanup();
            reject(createBridgeError('読み取り要求が中断されました。', 'ABORTED'));
        };

        const cleanup = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            window.removeEventListener('message', handleMessage);
            if (signal) {
                signal.removeEventListener('abort', handleAbort);
            }
        };

        const handleMessage = (event) => {
            if (event.source !== window) {
                return;
            }
            const payload = event.data;
            if (!payload || payload.source !== EXTENSION_MESSAGE_SOURCE) {
                return;
            }
            if (payload.type !== READ_UID_RESPONSE_TYPE || payload.requestId !== requestId) {
                return;
            }

            cleanup();
            if (!payload.ok) {
                reject(
                    createBridgeError(
                        payload.errorMessage || 'NFC読み取りに失敗しました。',
                        payload.errorCode || 'READ_FAILED'
                    )
                );
                return;
            }

            const normalizedUid = normalizeCardUid(payload.uid);
            if (!normalizedUid) {
                reject(
                    createBridgeError(
                        'カードUIDを取得できませんでした。',
                        'EMPTY_UID'
                    )
                );
                return;
            }
            resolve(normalizedUid);
        };

        timeoutId = setTimeout(() => {
            cleanup();
            reject(
                createBridgeError(
                    '拡張機能またはカード読み取りがタイムアウトしました。',
                    'BRIDGE_TIMEOUT'
                )
            );
        }, timeoutMs);

        window.addEventListener('message', handleMessage);
        if (signal) {
            signal.addEventListener('abort', handleAbort, { once: true });
        }
        window.postMessage(
            {
                source: WEB_MESSAGE_SOURCE,
                type: READ_UID_REQUEST_TYPE,
                requestId,
            },
            '*'
        );
    });
};
